import Handsontable from 'handsontable';

/**
 * Persistence for the user configured "decimal places" of the rework tables (e.g. activities).
 *
 * Column order, visibility and sorting are persisted by Handsontable's `persistentState` plugin and
 * re-applied automatically when the table initializes. The decimal places are a custom, non-plugin
 * setting, so the plugin does not restore them for us. We piggyback on the same persistentState storage
 * (see TableActivities.vue: `persistentStatePlugin.saveValue(PERSISTENT_STATE_DECIMAL_PLACES_KEY, ...)`)
 * and read the value back here to bake it into the columns *before* the table is created — mirroring how
 * the plugin applies order/visibility at init, and avoiding any post-init `updateSettings` (which would
 * reset the visibility/order the plugin just restored).
 *
 * The plugin stores each value as JSON under the localStorage key `${tableId}_${key}`.
 */
export const PERSISTENT_STATE_DECIMAL_PLACES_KEY = 'decimalPlaces';

export type DecimalPlacesByColumnData = Record<string, number>;

export function loadPersistedDecimalPlaces(tableId: string): DecimalPlacesByColumnData {
  const raw = localStorage.getItem(`${tableId}_${PERSISTENT_STATE_DECIMAL_PLACES_KEY}`);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object') return {};
    const result: DecimalPlacesByColumnData = {};
    Object.keys(parsed).forEach((columnData) => {
      if (typeof parsed[columnData] === 'number') result[columnData] = parsed[columnData];
    });
    return result;
  } catch {
    return {};
  }
}

/**
 * Returns a copy of the columns with the persisted decimal places applied (keyed by `column.data`).
 * Columns without a persisted value are returned unchanged.
 */
export function withPersistedDecimalPlaces<T extends Handsontable.ColumnSettings>(
  columns: T[],
  decimalPlaces: DecimalPlacesByColumnData,
): T[] {
  if (Object.keys(decimalPlaces).length === 0) return columns;
  return columns.map((column) => {
    if (typeof column.data === 'string' && typeof decimalPlaces[column.data] === 'number') {
      return { ...column, decimalPlaces: decimalPlaces[column.data] };
    }
    return column;
  });
}
